import {
    asyncListWithSkipTakeCreate,
    cachedValueCreateWithParamsV2,
    Evaluable,
    evaluateWhenFunction,
    hashFunctionCreate,
    stringReplaceEmptyOrWhiteSpaceValue,
    valueConvertWhenNotNullable
} from '@wix/devzai-utils-common';
import type {DoppeDtoBookingService, DoppeDtoBookingServiceType} from '../../../client-server-common/types/doppe-dto-booking-service';
import type {IDoppeViewerApi} from '../../../client-server-common/api/doppe-viewer-api';

/**
 * Creates a cached factory for fetching booking services async lists.
 * The factory caches async lists by search and serviceType parameters,
 * so multiple calls with the same params return the same cached list.
 * 
 * @param viewerApiClientGetter - A getter function for the viewer API client
 * @returns A cached value factory for services async lists
 * 
 * @example
 * ```ts
 * const servicesAsyncListFactory = useMemo(() => {
 *     return doppeViewerAsyncListFactoryServices(() => viewerApiClient);
 * }, [viewerApiClient]);
 * 
 * const servicesAsyncList = servicesAsyncListFactory.getValue({
 *     search: searchText,
 *     serviceType: DoppeDtoBookingServiceType.APPOINTMENT
 * });
 * ```
 */
export function doppeViewerAsyncListFactoryServices(
    viewerApiClientGetter: Evaluable<() => IDoppeViewerApi>
) {
    return cachedValueCreateWithParamsV2({
        paramsHashFunction: hashFunctionCreate<doppeViewerAsyncListFactoryServices.Params>({
            search: hashFunctionCreate(),
            serviceType: hashFunctionCreate(),
        }),
        paramsNormalizationFunction: params => ({
            ...params,
            search: valueConvertWhenNotNullable(params.search, s =>
                stringReplaceEmptyOrWhiteSpaceValue(s, undefined)
            )
        }),
        valueFunction: (params) => {
            const viewerApiClient = evaluateWhenFunction(viewerApiClientGetter);

            return asyncListWithSkipTakeCreate<DoppeDtoBookingService>({
                fetchFunction: async (skip, take) => {
                    return viewerApiClient.getBookingServices({
                        skip,
                        take,
                        search: params.search,
                        serviceType: params.serviceType,
                    });
                },
                defaultTake: 30,
            });
        }
    });
}

export namespace doppeViewerAsyncListFactoryServices {
    export interface Params {
        /**
         * Search query to filter services by name.
         */
        search?: string;
        
        /**
         * Filter services by type (APPOINTMENT, Class, COURSE).
         */
        serviceType?: DoppeDtoBookingServiceType;
    }
}
